import React, {useRef, useState, useEffect} from 'react';
import {Popper} from '@mui/material';
import {Svg} from "assets";
import moment from "moment";
import {TextToolTip} from "../table";

const activeStyles = {
    background: '#88c3e1',
    color: 'white',
};

export const TimeForTable = ({
                                 error,
                                 labelHeader,
                                 onChange,
                                 name,
                                 value,
                                 disabled,
                                 allowClearBtn,
                                 defaultValue,
                                 classNameWrapper,
                                 minTime,
                                 maxTime
                             }) => {
    const [time, setTime] = useState({hour: '', minute: ''});
    const [anchorEl, setAnchorEl] = useState(null);
    const [inputFocused, setInputFocused] = useState(false);
    const [selected, setSelected] = useState(false);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const hourRef = useRef(null);
    const minuteRef = useRef(null);

    const hours = Array.from({length: 24}, (_, i) => String(i).padStart(2, '0'));
    const minutes = Array.from({length: 60}, (_, i) => String(i).padStart(2, '0'));

    const convertTo24Hour = (t) => `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;

    useEffect(() => {
        const source = value || (defaultValue ? moment(defaultValue).format('HH:mm') : null);
        if (source) {
            const [h, m] = source.split(':');
            setTime({hour: String(h).padStart(2, '0'), minute: String(m).padStart(2, '0')});
        } else {
            setTime({hour: '', minute: ''});
        }
    }, [defaultValue, value, selected]);

    useEffect(() => {
        if (selected) {
            hourRef.current?.focus();
            setTime({hour: '', minute: ''});
        }
    }, [selected]);

    const isBeforeMinTime = (h, m) => {
        if (!minTime) return false;
        const [minH, minM] = minTime.split(':').map(Number);
        if (h < minH) return true;
        if (h === minH && m < minM) return true;
        return false;
    };

    const isAfterMaxTime = (h, m) => {
        if (!maxTime) return false;
        const [maxH, maxM] = maxTime.split(':').map(Number);
        if (h > maxH) return true;
        if (h === maxH && m > maxM) return true;
        return false;
    };


    const handleChange = (field, val) => {
        let cleaned = val.replace(/\D/g, '');
        if (field === 'hour' && (parseInt(cleaned, 10) > 23 || cleaned.length > 2)) return;
        if (field === 'minute' && (parseInt(cleaned, 10) > 59 || cleaned.length > 2)) return;

        setTime((prev) => {
            const updated = {...prev, [field]: cleaned};
            const h = parseInt(updated.hour, 10);
            const m = parseInt(updated.minute, 10);
            if (updated.hour.length === 2 && updated.minute.length === 2) {
                if (isBeforeMinTime(h, m) || isAfterMaxTime(h, m)) return prev;
            }
            return updated;
        });

        if (field === 'hour' && cleaned.length === 2) {
            minuteRef.current?.focus();
            setSelected(true);
        }
        if (field === 'minute' && cleaned.length === 0) {
            hourRef.current?.focus();
            setSelected(true);
        }
    };

    const sendTime = () => {
        if (time.hour.length === 2 && time.minute.length === 2) {
            const h = parseInt(time.hour, 10);
            const m = parseInt(time.minute, 10);
            if (isBeforeMinTime(h, m) || isAfterMaxTime(h, m)) return;
            onChange({
                target: {
                    name,
                    value: convertTo24Hour(time),
                },
            });
        }
    };

    const handleBackspace = (e, field) => {
        if (e.key === 'Backspace' && time[field]?.length === 0) {
            if (field === 'minute') setSelected(true);
        }
    };

    const handleClick = (event) => setAnchorEl(anchorEl ? null : event.currentTarget);

    const handleClose = () => {
        setAnchorEl(null);
        sendTime();
    };

    const selectFromPopper = (field, val) => {
        setTime((prev) => {
            const updated = {...prev, [field]: val};
            // if (updated.hour.length === 2 && updated.minute.length === 2) {
            //     onChange({
            //         target: {
            //             name,
            //             value: convertTo24Hour(updated),
            //         },
            //     });
            // }
            return updated;
        });
    };

    function convertTo12HourFormat(time24) {
        const [hourStr, minute] = time24.split(':');
        let hour = parseInt(hourStr, 10);
        const period = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        return {
            hour: String(hour).padStart(2, '0'),
            minute,
            period,
            formatted: `${String(hour).padStart(2, '0')}:${minute} ${period}`,
        };
    }

    const isHourDisabled = (h) => {
        const hNum = parseInt(h, 10);
        if (minTime) {
            const minH = parseInt(minTime.split(':')[0], 10);
            if (hNum < minH) return true;
        }
        if (maxTime) {
            const maxH = parseInt(maxTime.split(':')[0], 10);
            if (hNum > maxH) return true;
        }
        return false;
    };

    const isMinuteDisabled = (h, m) => {
        const hNum = parseInt(h, 10);
        const mNum = parseInt(m, 10);
        if (!h) return false;
        if (minTime) {
            const [minH, minM] = minTime.split(':').map(Number);
            if (hNum === minH && mNum < minM) return true;
        }
        if (maxTime) {
            const [maxH, maxM] = maxTime.split(':').map(Number);
            if (hNum === maxH && mNum > maxM) return true;
        }
        return false;
    };

    return (
        <div className={`time-input-wrapper ${classNameWrapper || ''}`} onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSelected(true);
            hourRef.current?.focus();
        }}>
            {labelHeader &&
                <p className={`input-main-label-header ${error?.type ? 'errorLabel' : ''}`}>{labelHeader}</p>}

            <div
                className={`time-input-box ${inputFocused ? 'time-input-focused' : ''} ${error?.type ? 'time-input-box-error' : ''} ${disabled ? 'time-input-box-disable' : ''}`}
                tabIndex={0}>
                {selected ? (
                    <div className='flex-all-center' onBlur={() => {
                        setSelected(false);
                        sendTime();
                    }}>
                        <input
                            className='time-input'
                            style={{width: '22px'}}
                            ref={hourRef}
                            type="text"
                            placeholder="hh"
                            value={time.hour}
                            onChange={(e) => handleChange('hour', e.target.value)}
                            maxLength={2}
                            disabled={disabled}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                        />
                        <span>:</span>
                        <input
                            className='time-input'
                            style={{width: '30px', marginLeft: 2}}
                            ref={minuteRef}
                            type="text"
                            placeholder="mm"
                            value={time.minute}
                            onChange={(e) => handleChange('minute', e.target.value)}
                            maxLength={2}
                            onKeyDown={(e) => handleBackspace(e, 'minute')}
                            disabled={disabled}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                        />
                    </div>
                ) : (
                    <div className='flex-all-center'>
                        <p style={{width: '22px', padding: '0 2px'}} className='time-input'
                           onClick={() => setTimeout(() => {
                               hourRef.current?.focus();
                           }, 100)}>
                            {(time.hour && time.minute) ? convertTo12HourFormat(`${time.hour}:${time.minute}`).hour : 'hh'}
                        </p>
                        <span style={{fontSize: 14}}>:</span>
                        <TextToolTip
                            onClick={() => setTimeout(() => {
                                minuteRef.current?.focus();
                            }, 100)}
                            text={(time.hour && time.minute) ? `${convertTo12HourFormat(`${time.hour}:${time.minute}`).minute} ${convertTo12HourFormat(`${time.hour}:${time.minute}`).period}` : 'mm'}
                            content={(time.hour && time.minute) ? `${convertTo12HourFormat(`${time.hour}:${time.minute}`).hour} ${convertTo12HourFormat(`${time.hour}:${time.minute}`).minute} ${convertTo12HourFormat(`${time.hour}:${time.minute}`).period}` : 'mm'}
                            wrapperStyles={{width: 'fit-content', marginLeft: 4}}
                        />
                    </div>
                )}

                <div className='align-center' style={{gap: 2, marginLeft: 4}}>
                    {(allowClearBtn && (time.hour || time.minute)) && (
                        <button className='reset-btn clear-button-time-for-table' type='button' onClick={(e) => {
                            e.stopPropagation();
                            setTime({hour: '', minute: ''});
                            onChange?.({target: {name, value: ''}});
                        }} style={{width: 18, height: 18}}>
                            <Svg.CloseSvg style={{width: '100%', height: '100%'}}/>
                        </button>
                    )}
                    <div onClick={(e) => {
                        e.stopPropagation();
                        handleClick(e);
                    }} style={{cursor: 'pointer'}} className='time-input-svg-block'>
                        <Svg.AccessTimeSvg/>
                    </div>
                </div>
            </div>

            <Popper id={id} open={open} anchorEl={anchorEl} className="custom-select-time-box" placement="bottom-start"
                    style={{position: 'absolute', zIndex: 2600}} onClick={(e) => e.stopPropagation()}>
                <div className="hour-minute-box" onMouseDown={e => e.preventDefault()}
                     onClick={(e) => e.stopPropagation()}>
                    <div>
                        {hours.map((item) => (
                            <button
                                key={`hour-${item}`}
                                onClick={() => selectFromPopper('hour', item)}
                                style={item === time.hour ? activeStyles : {}}
                                disabled={isHourDisabled(item)}
                                type="button"
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                    <div>
                        {minutes.map((item) => (
                            <button
                                key={`minute-${item}`}
                                onClick={() => selectFromPopper('minute', item)}
                                style={item === time.minute ? activeStyles : {}}
                                disabled={isMinuteDisabled(time.hour, item)}
                                type="button"
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>
            </Popper>

            {open && <div className="beck-drop" onClick={(e) => {
                e.stopPropagation();
                handleClose(e)
            }}/>}
        </div>
    );
};
